import { useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { configurerLeClient, quandLaSessionTombe } from '../shared/api/client';

/**
 * **Le démarrage : le client d'API configuré une fois, et le cache de données.**
 *
 * `configurerLeClient()` est appelé **au module et non dans un effet** : un effet
 * s'exécute après le premier rendu, donc une requête partie pendant ce rendu partirait
 * sans jeton et sans adresse de base.
 */
configurerLeClient();

function fabriquerLeCache(surSessionPerdue: () => void): QueryClient {
  const cache = new QueryClient({
    defaultOptions: {
      queries: {
        // **Aucun réessai automatique sur une erreur métier.** Les refus du produit sont
        // des `4xx` porteurs d'un code : les rejouer ne change rien et masque la cause.
        retry: (tentatives, erreur) => {
          const statut = (erreur as { statusCode?: number } | null)?.statusCode;
          if (typeof statut === 'number' && statut < 500) return false;
          return tentatives < 2;
        },
        refetchOnWindowFocus: false,
      },
    },
  });

  // **Le `401` vide le cache**, et pas seulement la session : les données déjà chargées
  // appartiennent à une personne qui n'est plus connectée. Les laisser en place les
  // ferait réapparaître à la connexion suivante, **possiblement celle de quelqu'un
  // d'autre sur le même poste** — un cours peut se tenir sur une machine partagée.
  quandLaSessionTombe(() => {
    cache.clear();
    surSessionPerdue();
  });

  return cache;
}

export function Fournisseurs({ children }: { readonly children: ReactNode }) {
  const [sessionTombee, poserSessionTombee] = useState(false);
  const [cache] = useState(() =>
    fabriquerLeCache(() => poserSessionTombee(true)),
  );

  return (
    <QueryClientProvider client={cache}>
      <ContexteDeSessionPerdue.Provider
        value={{
          sessionTombee,
          oublier: () => poserSessionTombee(false),
        }}
      >
        {children}
      </ContexteDeSessionPerdue.Provider>
    </QueryClientProvider>
  );
}

import { createContext, useContext } from 'react';

type EtatDeSessionPerdue = {
  readonly sessionTombee: boolean;
  readonly oublier: () => void;
};

const ContexteDeSessionPerdue = createContext<EtatDeSessionPerdue>({
  sessionTombee: false,
  oublier: () => {},
});

/**
 * **Dit à l'écran de connexion POURQUOI on y est revenu.**
 *
 * Sans cela, une personne dont la session est tombée se retrouve devant un formulaire
 * vide sans savoir si elle s'est déconnectée, si elle a été coupée, ou si le produit a
 * un défaut. **Le produit ne dit pas laquelle des trois causes s'applique** — le serveur
 * ne les distingue pas non plus —, mais il dit que c'est arrivé.
 */
export function useSessionTombee(): EtatDeSessionPerdue {
  return useContext(ContexteDeSessionPerdue);
}
